import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Scrubber as ScrubberSurface } from '@/beta/components/Scrubber';
import { PhaseRail, type PhaseTick } from '@/beta/components/PhaseRail';
import { getShowcaseBundle } from '@/beta/services/showcase';
import { usePreloadAdjacentPhases } from '@/beta/hooks/usePreloadAdjacentPhases';
import { useScrubberRevealAnimation } from '@/beta/hooks/useScrubberRevealAnimation';
import { labelForPhase } from '@/beta/lib/phase-labels';
import type { ShowcaseBundle } from '@/beta/types/showcase';

/** Scrubber page — `/beta/showcase/[vehicleId]/scrub`. Spec § 6.3.
 *
 *  Cinematic full-bleed photo with horizontal drag scrub. Phase rail fades
 *  up after 600ms (no chrome until first beat). ✕ or Escape closes back to
 *  Focus. Reuses the showcase bundle React Query cache populated by Focus. */
export function Scrubber() {
  const { vehicleId = '' } = useParams();
  const navigate = useNavigate();

  const query = useQuery<ShowcaseBundle>({
    queryKey: ['beta', 'showcase', vehicleId],
    queryFn: ({ signal }) => getShowcaseBundle(vehicleId, { signal }),
    enabled: vehicleId.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const phases = query.data?.phases ?? [];
  const [activeIndex, setActiveIndex] = useState(0);

  // Clamp activeIndex if phases shrink under us.
  useEffect(() => {
    if (phases.length === 0) {
      if (activeIndex !== 0) setActiveIndex(0);
      return;
    }
    if (activeIndex >= phases.length) {
      setActiveIndex(phases.length - 1);
    }
  }, [phases.length, activeIndex]);

  usePreloadAdjacentPhases({ phases, activeIndex });
  const { revealed } = useScrubberRevealAnimation();

  const ticks: PhaseTick[] = useMemo(
    () => phases.map((p) => ({ phase: p.phase, label: labelForPhase(p.phase) })),
    [phases],
  );

  const handleClose = useCallback(() => {
    if (window.history.state?.idx > 0) {
      navigate(-1);
    } else {
      navigate(`/beta/showcase/${vehicleId}`, { replace: true });
    }
  }, [navigate, vehicleId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  if (query.isLoading || !query.data) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'var(--vm-dark)',
          color: 'var(--vm-stone)',
          fontFamily: 'var(--vm-font-heading)',
          fontSize: 11,
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
        }}
      >
        Loading —
      </div>
    );
  }

  const vehicle = query.data.vehicle;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--vm-dark)',
        color: 'var(--vm-warm-white)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Photo (fills viewport) */}
      <div style={{ position: 'absolute', inset: 0 }}>
        {phases.length === 0 ? (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--vm-stone)',
              fontFamily: 'var(--vm-font-heading)',
              fontSize: 12,
              letterSpacing: '0.3em',
              textTransform: 'uppercase',
            }}
          >
            No phases yet
          </div>
        ) : (
          <ScrubberSurface
            phases={phases}
            activeIndex={activeIndex}
            alt={vehicle.display_name}
            onPhaseChange={setActiveIndex}
          />
        )}
      </div>

      {/* Top-left eyebrow + close button */}
      <div
        style={{
          position: 'absolute',
          top: 'var(--vm-s-3)',
          left: 'var(--vm-s-4)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--vm-s-3)',
          zIndex: 2,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 11,
            letterSpacing: '0.3em',
            textTransform: 'uppercase',
            color: 'var(--vm-red-on-dark)',
            textShadow: '0 1px 4px rgba(0,0,0,0.8)',
          }}
        >
          {`No. ${vehicle.vehicle_number} — ${vehicle.display_name}`}
        </span>
        <button
          type="button"
          aria-label="Close scrubber"
          onClick={handleClose}
          style={{
            background: 'transparent',
            border: '1px solid var(--vm-warm-white)',
            color: 'var(--vm-warm-white)',
            fontFamily: 'var(--vm-font-heading)',
            fontSize: 14,
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          ✕
        </button>
      </div>

      {/* Bottom phase rail */}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          background: 'linear-gradient(to top, rgba(0,0,0,0.55), transparent)',
          paddingTop: 'var(--vm-s-4)',
        }}
      >
        <PhaseRail
          ticks={ticks}
          activeIndex={activeIndex}
          onJump={setActiveIndex}
          hidden={!revealed}
        />
      </div>
    </div>
  );
}
